import '@f-fertighaus/layout/PageHeader/SSR/PageHeaderSSR'
import '@f/components/PageSharing'
import '@js/common/site-wide'
import '@js/common/request-flow-triggers'
import '@f/elements/AnchorsNavigation'
import '@f/widgets/CardCarousel/CardCarouselHTML'
import '@f-fertighaus/widgets/FloatingBanner'
import '@f/wagtail-blocks/Carousel'
import './partials/SaleOffices'

import { ClickAwayListener, useMediaQuery } from '@mui/material'
import React, { useState } from 'react'
import { createRoot } from 'react-dom/client'

import { get, QueryParams } from '@js/common/api'
import { getFormattedValueWithThousandSeparator } from '@js/common/getFormattedValueWithThousandSeparator'
import { getUrl } from '@js/common/getUrlByDomain'
import User from '@js/common/user'

import Modal from '@f/components/Modal'
import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

import {
  ChatAssistant,
  chatStyles
} from '@f-fertighaus/applications/ChatAssistant/ChatAssistant'
import { SetLocationModal } from '@f-fertighaus/applications/SearchResults/partials/items/SetLocationModal'
import { getRatingText } from '@f-fertighaus/templates/VendorDetailPage/partials/ReviewsSection/ReviewsSectionVendorSummary'
import { handleLineClampAdjustment } from '@f-fertighaus/utils/handleLineClampAdjustment'

import { VendorHeadquarters } from './partials/VendorHeadquarters'

void (async function () {
  const custom_houses_containers = [
    ...document.querySelectorAll('.js-custom-houses')
  ]
  if (!custom_houses_containers.length) {
    return
  }
  const { default: CardCarousel } = await import('@f/widgets/CardCarousel')
  custom_houses_containers.forEach(housesContainer => {
    const params = housesContainer.dataset.params
    const vendorNid = housesContainer.dataset.vendorNid
    const container = housesContainer.querySelector(
      '.js-custom-houses-container'
    )

    const deleteContainer = containerToDelete => {
      containerToDelete.parentElement.removeChild(containerToDelete)
    }

    if (params) {
      const paramsObj = {}
      params.split('&').forEach(paramPair => {
        paramsObj[paramPair.split('=')[0]] = paramPair.split('=')[1]
      })

      const queryParams = {
        ...paramsObj,
        vendor_nid__in: vendorNid
      }

      get('houses', {
        ...paramsObj,
        vendor_nid__in: vendorNid,
        limit: 4
      }).then(data => {
        if (data.results.length > 0) {
          const root = createRoot(container)
          root.render(
            <CardCarousel
              items={data.results}
              item_nid_type='house_nid'
              card='house-small'
              main_cta_link={`${getUrl(
                'houseList'
              )()}${QueryParams.paramsToQueryString(queryParams)}`}
              main_cta_text={housesContainer.dataset.moreHousesText}
              main_cta_type='block-secondary'
            />
          )
        } else {
          deleteContainer(housesContainer)
        }
      })
    } else {
      deleteContainer(housesContainer)
    }
  })
})()

/* Close Inactive Vendor message */
void (function () {
  const inactiveVendorMessage = document.querySelector('#InactiveVendorMessage')
  if (inactiveVendorMessage) {
    const closeButton = inactiveVendorMessage.querySelector(
      '.js-inactive-vendor-message-close'
    )

    const removeButton = () => {
      closeButton.removeEventListener('click', removeButton)
      inactiveVendorMessage.parentNode.removeChild(inactiveVendorMessage)
    }
    closeButton.addEventListener('click', removeButton)
  }
})()

/* Vendor Microsite Mobile Menu */
void (function () {
  const menuMountPoint = document.querySelector('.js-ms-menu-toggle')

  if (menuMountPoint) {
    let menuItems = []
    try {
      menuItems = JSON.parse(menuMountPoint.dataset.menuItems)
    } catch (e) {}

    const VendorMicrositeMobileMenu = props => {
      const [isActive, setIsActive] = useState(false)

      const handleOutsideClick = () => {
        if (isActive) {
          setIsActive(false)
        }
      }

      return (
        <ClickAwayListener onClickAway={handleOutsideClick}>
          <div className='f-pos-rel'>
            <CTA
              type='icon'
              tag='button'
              className={`[ f-pos-rel f-p-a-010 f-m-v-010 ] VendorMicrosite-menu-button ${
                isActive ? 'is-open' : ''
              }`}
              onClick={() => setIsActive(active => !active)}>
              <Icon icon='menu-16px' className='[ f-icon-030 f-c-st-whi ]' />
            </CTA>
            {isActive ? (
              <nav className='[ f-c-ba-whi f-shadow ] VendorMicrosite-menu'>
                <ul className='f-!list'>
                  {props.menuItems.map(menuItem => (
                    <li
                      key={`vendor-ms-menu-${menuItem.url.replace('/', '-')}`}>
                      <CTA
                        tag='a'
                        href={`${menuItem.url}#nav`}
                        type='text-primary'
                        className='[ f-t-body f-p-a-020 ] PageHeader-menu-button'>
                        {menuItem.title}
                      </CTA>
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
        </ClickAwayListener>
      )
    }
    const root = createRoot(menuMountPoint)
    root.render(<VendorMicrositeMobileMenu menuItems={menuItems} />)
  }
})()

/* Vendor Microsite Search */
void (async function () {
  const vendorHouseSearch = document.querySelector('#VendorMicrositeSearch')

  if (vendorHouseSearch) {
    const { default: SearchResults } = await import(
      '@f-fertighaus/applications/SearchResults'
    )
    const { default: RegionalPricesInfo } = await import(
      '@f-fertighaus/applications/RegionalPricesInfo'
    )
    const { FinanceEntryPanel } = await import(
      '@f-fertighaus/applications/FinanceEntry'
    )
    const { default: BudgetCalculatorModal } = await import(
      '@f-fertighaus/applications/BudgetCalculatorModal'
    )
    const openBauortModal = () => {
      const modal = new Modal()
      modal.openModal({
        component: <RegionalPricesInfo />
      })
    }

    const openBudgetOptions = event => {
      event?.preventDefault()

      const modal = new Modal()

      const openFlow = flowType => {
        let nextModal = new Modal()

        if (flowType === 'budget_calculator') {
          nextModal.openModal({
            component: (
              <BudgetCalculatorModal {...props.sherlock.BudgetCalculator} />
            )
          })
        }
      }

      modal.openModal({
        component: <FinanceEntryPanel openFlow={openFlow} />,
        options: {
          className: 'ModalFinance'
        }
      })
    }

    const openSetLocationModal = () => {
      const modal = new Modal()
      modal.openModal({
        component: <SetLocationModal />
      })
    }

    const availableFilters = [
      {
        name: 'price',
        label: 'Preis',
        type: 'price',
        values: {
          ...window.SherlockContainer.houseFilter.defaultPrice,
          max: 500000
        },
        suffix: '€',
        default: 'price',
        feature: {
          label: 'Budgethilfe',
          target: openBudgetOptions,
          wtData: {
            'CTA-click': {
              label: 'modal-finance-options',
              location: 'filter-panel'
            }
          }
        }
      },
      {
        name: 'delivery_regions',
        label: 'Bauort',
        type: 'bauort',
        info:
          'Wir empfehlen Ihnen den Bauort für verbesserte Preisangaben einzutragen, da diese sich je nach Umgebung unterscheiden.',
        feature: !User.getLocation().postcode
          ? {
              label: 'Genauere Preisangaben erhalten',
              target: openBauortModal,
              wtData: {
                'CTA-click': {
                  label: 'modal-bauort-regional',
                  location: 'filter-panel'
                }
              }
            }
          : null
      },
      {
        name: 'expansion_stage',
        label: 'Ausbaustufe',
        type: 'radio',
        default: 'price',
        values: window.SherlockContainer.houseFilter.defaultExpansionStages,
        info:
          'Zustand eines Hauses bei der Übergabe an den Bauherren. Sie hat daher Auswirkung auf den Hauspreis.'
      },
      {
        name: 'construction_method',
        label: 'Bauweise',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultConstructionMethods,
        info:
          'Die Bauweise beschreibt, in welcher Konstruktion und auf welche Weise das Eigenheim errichtet wird.'
      },
      {
        name: 'living_area_total',
        label: 'Wohnfläche',
        type: 'range',
        values: window.SherlockContainer.houseFilter.defaultLivingArea,
        suffix: 'm²'
      },
      {
        name: 'number_rooms',
        label: 'Zimmeranzahl',
        type: 'range',
        values: window.SherlockContainer.houseFilter.defaultRooms,
        labelSuffix: 'Zimmer'
      },
      {
        name: 'form',
        label: 'Haustyp',
        labelPlural: 'Haustypen',
        type: 'checkbox',
        values: [
          { id: 2, name: 'Satteldach-Klassiker' },
          { id: 1, name: 'Bungalow' },
          { id: 4, name: 'Stadtvilla' },
          { id: 3, name: 'Blockhaus' },
          { id: 7, name: 'Fachwerk' },
          { id: 5, name: 'Cubushaus' },
          { id: 6, name: 'Pultdachhaus' }
        ]
      },
      {
        name: 'roof_shape',
        label: 'Dachform',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultRoofShapes
      },
      {
        name: 'styles',
        label: 'Stil',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultStyles
      },
      {
        name: 'utilization',
        label: 'Hausart',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultUtilization
      },
      {
        name: 'energy_standards',
        label: 'Energiestandard',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultEnergyStandards,
        info: 'Je besser die Dämmung, desto besser ist der Energiestandard.'
      },
      {
        name: 'number_storeys',
        label: 'Etagen',
        type: 'range',
        values: window.SherlockContainer.houseFilter.defaultFloors,
        labelSuffix: 'Etagen'
      },
      {
        name: 'target_groups',
        label: 'Besondere Anforderungen',
        type: 'checkbox',
        values: window.SherlockContainer.houseFilter.defaultTargetGroups
      }
    ]

    const availableSorting = [
      {
        label: 'Standard Sortierung',
        value: '',
        isDefault: true
      },
      {
        label: 'Wohnfläche aufsteigend',
        value: 'living_area_total'
      },
      {
        label: 'Wohnfläche absteigend',
        value: '-living_area_total'
      },
      {
        label: 'Preis aufsteigend',
        value: 'price'
      },
      {
        label: 'Preis absteigend',
        value: '-price'
      }
    ]
    const root = createRoot(vendorHouseSearch)
    root.render(
      <SearchResults
        type={'houses'}
        openSetLocationModal={openSetLocationModal}
        availableFilters={availableFilters}
        availableSorting={availableSorting}
        disableBanners={true}
        forcedParams={[
          {
            name: 'vendor_nid',
            value: vendorHouseSearch.dataset.vendorNid
          }
        ]}
        disableTopPlacementAndBranding={true}
        location='house-list-microsite'
      />
    )
  }
})()

/* Team section */

void (async () => {
  const mountPoint = document.getElementById('VendorDetailPage-team')

  if (mountPoint) {
    const { default: SalesAgentsTeam } = await import(
      './partials/SalesAgentsTeam'
    )
    mountPoint.innerHTML = ''
    const root = createRoot(mountPoint)
    root.render(<SalesAgentsTeam vendorNid={mountPoint.dataset.vendorNid} />)
  }
})()

const CAROUSEL_HOUSES_SELECTOR = 'a[data-item-nid]'

function updateToRegional(price) {
  return renderedPrice => {
    if (price && renderedPrice) {
      renderedPrice.textContent = `ab ${getFormattedValueWithThousandSeparator(
        price
      )} €`
    }
  }
}

void (function replaceHousePricesWithRegional() {
  const postcode = User.getLocation().postcode

  if (postcode) {
    const houses = document.querySelectorAll(CAROUSEL_HOUSES_SELECTOR)
    const houseNids = Array.from(houses).map(h => h.dataset.itemNid)

    get('houses', {
      nid__in: houseNids.join(),
      delivery_regions__in: postcode,
      limit: houseNids.length
    }).then(res => {
      res.results.forEach(house => {
        const CAROUSEL_HOUSE_PRICE_SELECTOR = `a[data-item-nid='${house.nid}'] .Card-info-price > span:first-child`

        const renderedPrices = document.querySelectorAll(
          CAROUSEL_HOUSE_PRICE_SELECTOR
        )

        Array.from(renderedPrices).forEach(
          updateToRegional(house.price_display)
        )
      })
    })
  }
})()

void (async function addStarRating() {
  const STAR_RATING_SELECTOR = '.js-vendor-rating'

  const nodes = document.querySelectorAll(STAR_RATING_SELECTOR)

  if (!nodes.length) {
    return
  }
  const { Rating } = await import(
    '@f-fertighaus/components/ReviewRating/SearchResultRating'
  )
  nodes.forEach(node => {
    const root = createRoot(node)
    const { rating, count } = node.dataset

    root.render(
      <>
        <div>
          <Rating
            value={Number(rating.replace(',', '.'))}
            size={24}
            style={{ strokeWidth: 0.5 }}
          />
        </div>
        <span className='ReviewRating_avg-rating'>{rating}</span>
        <span className='RevieRating_total-count'>({count})</span>
      </>
    )
  })
})()

void (async function addReviewsSection() {
  const ROOT_SELECTOR = 'js-vendor-reviews'

  const node = document.getElementById(ROOT_SELECTOR)
  if (!node) {
    return
  }
  const { ReviewsSection } = await import(
    '@f-fertighaus/templates/VendorDetailPage/partials/ReviewsSection/ReviewsSection'
  )
  const root = createRoot(node)
  const { vendorId } = node.dataset

  root.render(<ReviewsSection vendorId={vendorId} />)
})()

void (function addResponseRate() {
  const ROOT_SELECTOR = 'js-vendor-response-rate'

  const element = document.getElementById(ROOT_SELECTOR)

  if (!element) {
    return
  }

  const root = createRoot(element)
  const { rating } = element.dataset

  root.render(<div>{getRatingText(Number(rating))}</div>)
})()

function adjustDescriptionClampMosaicGallery() {
  const imageDescriptions = document.querySelectorAll(
    '.VendorDetailPage-mosaic-gallery_description'
  )

  if (imageDescriptions.length === 0) {
    return
  }
  imageDescriptions.forEach(imageDescription => {
    handleLineClampAdjustment({
      containerElement: imageDescription,
      spacingAroundContainer: 32,
      spacingBelowTitle: 8
    })
  })
}

void (function () {
  const galleryContainer = document.querySelector(
    '.VendorDetailPage-mosaic-gallery_container'
  )

  if (!galleryContainer) {
    return
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          adjustDescriptionClampMosaicGallery()
          observer.disconnect()
        }
      })
    },
    { rootMargin: '100px 0px', threshold: 0.01 }
  )

  observer.observe(galleryContainer)
})()

const ChatAssistantWrapper = props => {
  const isMobileView = useMediaQuery('(max-width: 900px)')

  return (
    <ChatAssistant
      vendorNid={props.vendorNid}
      vendorName={props.vendorName}
      vendorSlug={props.vendorSlug}
      wtLabel='chat-icon-vendor-detail'
      style={{
        ...chatStyles.branded,
        chatButtonStyle: {
          ...chatStyles.branded.chatButtonStyle,
          ...(isMobileView && {
            padding: '16px 8px',
            height: '48px'
          })
        }
      }}
    />
  )
}

void (() => {
  const chatMountPoint = document.getElementById('chat-assistant')
  const vendorNid = chatMountPoint?.dataset?.vendornid
  const vendorName = chatMountPoint?.dataset?.vendorname
  const vendorSlug = chatMountPoint?.dataset?.slug

  if (chatMountPoint) {
    const root = createRoot(chatMountPoint)
    root.render(
      <ChatAssistantWrapper
        vendorNid={vendorNid}
        vendorName={vendorName}
        vendorSlug={vendorSlug}
      />
    )
  }
})()

void (() => {
  const node = document.getElementById('vendor-headquarters')

  if (node) {
    const root = createRoot(node)
    root.render(
      <VendorHeadquarters
        vendorName={node.dataset.vendorName}
        vendorNid={node.dataset.vendorNid}
      />
    )
  }
})()
